'use client';

import { useEffect, useState } from 'react';
import { Dialog, DialogContent, useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import type { SparePart } from '../../api/spareparts-api';
import {
  lookupProducts,
  SPAREPARTS_API_KEY,
  type ProductsLookupRequest,
} from '../../api/spareparts-api';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import {
  AddToCartVariantWithVariantDialog,
  type VariantData,
} from '../product/dialog/add-to-cart-variant-dialog-with-variant';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

interface SparePartDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sparePart: SparePart | null;
}

export function SparePartDialog({ open, onOpenChange, sparePart }: SparePartDialogProps) {
  const locale = useLocaleStore(state => state.currentLocale);
  const openDialog = useDialogStore(state => state.open);
  const [lookupResult, setLookupResult] = useState<VariantData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslations();

  // Auto-lookup when dialog opens with spare part
  useEffect(() => {
    if (open && sparePart?.article?.articleNumber) {
      performAutoLookup(sparePart.article.articleNumber);
    }
  }, [open, sparePart]);

  const performAutoLookup = async (articleNumber: string) => {
    setIsLoading(true);
    try {
      const request: ProductsLookupRequest = {
        type: 'code',
        term: articleNumber,
      };

      const response = await lookupProducts({
        body: request,
        apiKey: SPAREPARTS_API_KEY,
        locale,
      });

      // Check if we got a variant result
      if (
        (response && (response as any)['App\\Models\\Variant']) ||
        (response as any)['App\\Models\\Product']
      ) {
        const variantData = ((response as any)['App\\Models\\Variant'] ??
          (response as any)['App\\Models\\Product']) as VariantData;
        setLookupResult(variantData);
        // Open the add to cart dialog with the variant
        openDialog({
          id: AddToCartVariantWithVariantDialog.id,
          content: (
            <AddToCartVariantWithVariantDialog
              variant={variantData}
              quantity={sparePart?.quantity || 1}
            />
          ),
        });
        // Close the spare part dialog since we're showing the add to cart dialog
        onOpenChange(false);
      } else {
        // If no variant found, show the "not available" message
        setLookupResult(null);
      }
    } catch (err) {
      console.error('Error during auto-lookup:', err);
      // If lookup fails, show the "not available" message
      setLookupResult(null);
    } finally {
      setIsLoading(false);
    }
  };

  // If we have a lookup result, show the add to cart dialog instead
  if (lookupResult) {
    return null; // The add to cart dialog is already open
  }

  if (!sparePart) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        {isLoading && (
          <div className="flex items-center justify-center py-4">
            <div className="text-sm text-gray-500">{t('text3131')}</div>
          </div>
        )}

        {!isLoading && !lookupResult && (
          <div className="flex items-center justify-center py-4">
            <div className="text-2xl font-semibold text-destructive-foreground">
              {t('text3130')}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
